import React, { useEffect } from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { OMGTheme } from '@ordermygear/theme';
import Button from '@ordermygear/button';
import Layout from '../components/common/Layout';
import Dashboard from './Dashboard';
import {
  Sale,
  Dealer,
  Order
} from '../../../backend/shared/types';
import { H2, Text } from '@ordermygear/typography';

const StatusFormContainer = styled.form`
  min-height: 500px;
  width: 100%;
  max-width: ${OMGTheme.breakpoints.xs};
  margin: auto;
  .error {
    font-size: .8em;
    color: #FF0000;
    display: block;
    margin: 8px auto;
  }
  h2 {
    color: ${OMGTheme.colors.grayDark};
    font-size: 24px;
    font-weight: 500;
    margin-bottom: 20px;
    margin-top: 20%;
    line-height: 1;
  }
  label {
    color: ${OMGTheme.colors.grayDark};
    font-size: 12px;
    margin-bottom: 8px;
    margin-top: 0px;
    display: block;
    line-height: 1;
  }
  > button {
    width: 100%;
  }
  input {
    width: 100%;
    height: 42px;
    border-radius: 5px;
    border: 1px solid ${OMGTheme.colors.grayLightBorder};
    font-size: 14px;
    padding-left: 10px;
    outline: none;
  }
  .is-valid:focus {
    border: 1px solid ${OMGTheme.colors.primary };
  }
  .is-not-valid:focus {
    border: 1px solid ${OMGTheme.colors.alertBorder };
  }
`;

const Spacer = styled.div`
  padding-top: 10px;
  margin-bottom: 20px;
`;

const SubmitError = styled.div`
  color: ${OMGTheme.colors.alert};
  font-weight: 500;
  padding-top: 10px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const Index = () => {
  const [sale, setSale] = useState<Sale | null>(null);
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState<Order | null>(null);
  const [orderId, setOrderId] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    const getValues = async () => {
      const response = await fetch('/orderstatus/api/landing');
      if (!response.ok) {
        const { error } = await response.json();
        setSubmitErr(error);
        return;
      }
      const responseJson = await response.json();
      const { sale, dealer } = responseJson;
      if (sale) {
        setSale(sale);
      }
      setDealer(dealer);
    };
    getValues();
  }, []);

  const validateOrderId = () => {
    return orderId.match('^[0-9]+$') !== null;
  }

  const validateEmail = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  const getClassName = (key: string) => {
    if (key === 'email') {
      if (email !== '') {
        return validateEmail() ? 'is-valid' : 'is-not-valid';
      } else {
        return '';
      }
    } else {
      if (orderId !== '') {
        return validateOrderId() ? 'is-valid' : 'is-not-valid';
      } else {
        return '';
      }
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setSubmitErr(''); // unset if first attempt failed
    try {
      const orderResponse = await fetch(`/orderstatus/api/order/${orderId}/${encodeURIComponent(email)}`);
      if (!orderResponse.ok) {
        if (orderResponse.status >= 400) {
          const { error } = await orderResponse.json();
          setSubmitErr(error);
        } else {
          setSubmitErr('There was an error getting the order.');
        }
        setOrderId('');
        setEmail('');
        setLoading(false);
        return;
      }
      const orderResponseJson = await orderResponse.json();
      const { order } = orderResponseJson;
      setOrder(order);
      if (!sale) {
        const saleResponse = await fetch(`/orderstatus/api/sale/${order.links.sale}`);
        if (!saleResponse.ok) {
          setSubmitErr('Error: could not find the sale.');
          setLoading(false);
          return;
        }
        const saleResponseJson = await saleResponse.json();
        const { sale } = saleResponseJson;
        setSale(sale);
      }
      setLoading(false);
      setSubmitted(true);
    } catch (err) {
      setSubmitted(false);
      setSubmitErr('There was an error getting the order.');
      setLoading(false);
      return;
    }
  }

  if (!dealer) {
    return (<H2 align="center">Loading...</H2>);
  } else if (loading) {
    return (
      <Layout sale={sale} dealer={dealer}>
        <>
          <br/>
          <H2 align="center">Loading Order Data...</H2>
        </>
      </Layout>
    );
  } else if (sale && !sale.order_tracking) {
    return (
      <Layout sale={sale} dealer={dealer}>
        <>
          <br/>
          <H2 align="center">Order tracking is not enabled for this store.</H2>
          <Text color={OMGTheme.colors.primary} weight="bold" align="center"><a href="/">Back to store</a></Text>
        </>
      </Layout>
    );
  } else if (order && order.is_deleted) {
    return (
        <Layout sale={sale} dealer={dealer}>
          <>
            <br/>
            <H2 align="center">This order has been deleted.</H2>
            <Text color={OMGTheme.colors.primary} weight="bold" align="center"><a href="/">Back to store</a></Text>
          </>
        </Layout>
    );
  } else if (!submitted  || submitErr) {
    return (
      <Layout sale={sale} dealer={dealer}>
        <StatusFormContainer>
          <h2>Check Order Status</h2>
          <label htmlFor="email">Email Address</label>
          <input
            name="email"
            type="text"
            value={email}
            className={getClassName('email')}
            placeholder="Enter your email address"
            onChange={(e) => setEmail(e.target.value)}
          />
          <Spacer />
          <label htmlFor="orderNumber">Order Number</label>
          <input
            name="orderNumber"
            type="text"
            value={orderId}
            className={getClassName('orderId')}
            placeholder="Enter your order number"
            onChange={(e) => setOrderId(e.target.value)}
          />
          <Spacer />
          <Button
            disabled={!validateEmail() || !validateOrderId() || loading}
            onClick={(e) => handleSubmit(e)}
            color="secondary"
          >
            Sign In
          </Button>
          <SubmitError>{submitErr ?? null}</SubmitError>
        </StatusFormContainer>
      </Layout>
    );
  } else {
    return (
      <Layout sale={sale} dealer={dealer}>
        <Dashboard sale={sale} dealer={dealer} order={order} />
      </Layout>
    )
  }
};

export default Index;